import { Permission } from '@Constants/Permission.g';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useHasPermission } from '@Contexts/appContext';
import { ApiLedgerAccount } from '@Model';
import {
    Card,
    Checkbox,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumns,
    IFrameBodyProps,
    IFrameEditShellProps,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useParams } from 'react-router';

export function FrameEditLedgerAccount(props: IFrameEditShellProps<ApiLedgerAccount>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiLedgerAccount>
            apiResourcePath={`${STAMMDATEN_API}/ledger_accounts`}
            renderer={FrameEditLedgerAccountBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenLedgerAccountUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenLedgerAccountCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenLedgerAccountDelete)}
            frameProps={{ events: { ...frameEditBaseNavigationEvents, ...props.events } }}
        />
    );
}

function FrameEditLedgerAccountBody(props: IFrameBodyProps<ApiLedgerAccount>) {
    const i18n = useTranslate('');

    const binder = useGuiBinder<ApiLedgerAccount>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Sachkonto', props.mode),
                resourceLabel: 'Sachkonto',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="PrimaryData" border={'withoutBorder'}>
                <Grid>
                    <GridColumns spans={[4, 4, 4]}>
                        <TextInput {...binder.bindToProp<string>('number')} label={i18n('Nummer')} />
                        <TextInput {...binder.bindToProp<string>('label')} label={i18n('Bezeichnung')} />
                        <TextInput {...binder.bindToProp<string>('shorthand')} label={i18n('Suchcode')} />
                    </GridColumns>
                </Grid>
                <TextInput
                    {...binder.bindToProp<string>('reverse_charge_phrase')}
                    label={i18n('Abweichender RC-Satz')}
                    inputSize={3}
                />
                <TextInput {...binder.bindToProp<string>('remarks')} inputSize={3} label={i18n('Bemerkung')} />
            </Card>
            <Card name="AutomaticFlag" label={i18n('FiBu Einstellungen')} border={'withoutBorder'}>
                <Checkbox {...binder.bindToAutoConfirmed<boolean>('is_automatic')} label={i18n('Automatik Konto')} />
            </Card>
        </EditFrameShell>
    );
}
